import { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../config';
import { useAppResume } from './useAppResume';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

interface SubscriptionTier {
  tier: string;
  display_name: string;
  description: string;
  price_cents: number;
  price_interval: string;
  item_limit: number;
  ai_scan_limit_per_month: number;
  storage_limit_mb: number;
  shared_collections_limit: number;
  groups_limit: number;
  group_members_limit: number;
  features: string[];
  bonus_cap_items?: number;
  bonus_cap_ai_scans?: number;
  bonus_cap_storage_mb?: number;
}

interface Subscription {
  id: number;
  user_id: number;
  tier: string;
  status: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  payment_source?: 'stripe' | 'ios_iap' | 'android_iap' | null;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  bonus_items: number;
  bonus_ai_scans: number;
  bonus_storage_mb: number;
  created_at: string;
  updated_at: string;
}

interface UsageInfo {
  items: {
    used: number;
    limit: number;
    remaining: number;
  };
  ai_scans_per_month: {
    used: number;
    limit: number;
    remaining: number;
  };
  storage_mb: {
    used: number;
    limit: number;
    remaining: number;
  };
  total_ai_scans: number;
  last_ai_scan_at: string | null;
}

interface SubscriptionData {
  subscription: Subscription;
  tier_info: SubscriptionTier;
  limits: {
    items: number;
    ai_scans_per_month: number;
    storage_mb: number;
    shared_collections: number;
    groups: number;
    group_members: number;
  };
  usage: UsageInfo;
}

export const useSubscription = () => {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/subscription`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch subscription');
      }

      const data = await response.json();
      setSubscriptionData(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError(err.message || 'Failed to fetch subscription');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Refresh subscription when app resumes from background (iOS/Android)
  useAppResume(
    useCallback(() => {
      console.log('[useSubscription] App resumed, refreshing subscription');
      fetchSubscription();
    }, [fetchSubscription]),
    30000 // 30 seconds minimum background time
  );

  const canUseFeature = useCallback((feature: string): boolean => {
    if (!subscriptionData) return false;
    const features = subscriptionData.tier_info.features || [];
    return features.includes('all') || features.includes(feature);
  }, [subscriptionData]);

  const hasReachedLimit = useCallback((resource: 'items' | 'ai_scans_per_month' | 'storage_mb'): boolean => {
    if (!subscriptionData) return true;
    const usage = subscriptionData.usage[resource];
    return usage.used >= usage.limit;
  }, [subscriptionData]);

  const getUsagePercentage = useCallback((resource: 'items' | 'ai_scans_per_month' | 'storage_mb'): number => {
    if (!subscriptionData) return 0;
    const usage = subscriptionData.usage[resource];
    if (usage.limit === Infinity || usage.limit === -1) return 0;
    return Math.min(100, Math.round((usage.used / usage.limit) * 100));
  }, [subscriptionData]);

  const isUnlimited = useCallback((resource: string): boolean => {
    if (!subscriptionData) return false;
    const limit = subscriptionData.limits[resource as keyof typeof subscriptionData.limits];
    return limit === Infinity || limit === -1;
  }, [subscriptionData]);

  const formatLimit = useCallback((limit: number): string => {
    if (limit === Infinity || limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  }, []);

  const formatStorage = useCallback((mb: number): string => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(mb)} MB`;
  }, []);

  const getPriceDisplay = useCallback((tier?: SubscriptionTier): string => {
    const t = tier || subscriptionData?.tier_info;
    if (!t) return '';
    
    if (t.price_cents === 0) return 'Free';
    
    const dollars = t.price_cents / 100;
    const interval = t.price_interval === 'year' ? 'year' : 'month';
    
    return `$${dollars.toFixed(2)}/${interval}`;
  }, [subscriptionData]);

  const hasSubscriptionIssue = useCallback((): boolean => {
    if (!subscriptionData) return false;

    const { subscription, usage } = subscriptionData;

    // Over item limit (only if there is a finite limit)
    const itemsLimit = usage.items.limit;
    const overItemLimit =
      typeof itemsLimit === 'number' &&
      itemsLimit > 0 &&
      itemsLimit !== -1 &&
      usage.items.used >= itemsLimit;

    // Treat only real billing problems as issues, not cleanly canceled/free
    const problematicStatus = subscription.status === 'past_due' || subscription.status === 'unpaid';

    return overItemLimit || problematicStatus;
  }, [subscriptionData]);

  return {
    // Data
    subscription: subscriptionData?.subscription,
    tierInfo: subscriptionData?.tier_info,
    limits: subscriptionData?.limits,
    usage: subscriptionData?.usage,
    
    // State
    loading,
    error,
    
    // Methods
    refresh: fetchSubscription,
    canUseFeature,
    hasReachedLimit,
    getUsagePercentage,
    isUnlimited,
    formatLimit,
    formatStorage,
    getPriceDisplay,
    hasSubscriptionIssue,
  };
};

export const useSubscriptionPlans = () => {
  const [plans, setPlans] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/subscription/plans`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch plans');
        }
        
        const data = await response.json();
        setPlans(data.plans);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching plans:', err);
        setError(err.message || 'Failed to fetch plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return { plans, loading, error };
};

export default useSubscription;
